import { useEffect, useState } from "react";
import toast from "../../../../utils/toast";
import {
  getVacateNoticeData,
  createVacateNotice,
  cancelVacateNotice,
  getVacateNoticeSetting,
} from "../../../../services/tenant/tenantServices";

export default function VacateNotice() {
  const [vacateData, setVacateData] = useState(null);
  const [vacateNoticeSettingData, setVacateNoticeSettingData] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [isModalOpenCancel, setIsModalOpenCancel] = useState(false);

  const [form, setForm] = useState({
    plannedLeaveDate: "",
    reasonForVacating: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const minDateStr = vacateNoticeSettingData?.earliestLeaveDate
    ? vacateNoticeSettingData.earliestLeaveDate.split("T")[0]
    : (() => {
        const d = new Date();
        d.setDate(d.getDate() + 15);
        return d.toISOString().split("T")[0];
      })();

  const minDateFormatted = minDateStr
    ? new Date(minDateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

  useEffect(() => {
    fetchVacateNotice();
    fetchVacateNoticeSettingData();
  }, []);

  const fetchVacateNoticeSettingData = async () => {
    try {
      const res = await getVacateNoticeSetting();
      if (res.statusCode === 1 && res.data) {
        setVacateNoticeSettingData(res.data);
      }
    } catch (err) {
      console.log(
        err.response?.data?.message || "Failed to fetch setting data",
      );
    }
  };

  const fetchVacateNotice = async () => {
    setPageLoading(true);
    try {
      const res = await getVacateNoticeData();
      if (res.statusCode === 1 && res.data) {
        setVacateData(res.data);
      } else {
        setVacateData(null);
      }
    } catch (err) {
      setVacateData(null);
    } finally {
      setPageLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    if (status === 1)
      return { label: "⏳ Pending", color: "var(--amber)", bg: "#FEF3C7" };
    if (status === 2)
      return { label: "✓ Approved", color: "#27500A", bg: "#EAF3DE" };
    if (status === 3)
      return { label: "✕ Rejected", color: "#A32D2D", bg: "#FCEBEB" };
    return { label: "○ Unknown", color: "#6B7280", bg: "#F3F4F6" };
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };
  const handleSubmit = async () => {
    const e = {};
    if (!form.plannedLeaveDate) {
      e.plannedLeaveDate = "Planned leave date is required.";
    } else {
      const selectedDate = new Date(form.plannedLeaveDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const minDays = vacateNoticeSettingData?.minNoticeDays ?? 15;
      const diffDays = Math.floor(
        (selectedDate - today) / (1000 * 60 * 60 * 24),
      );
      if (diffDays < minDays) {
        e.plannedLeaveDate = `Leave date must be at least ${minDays} days from today.`;
      }
    }
    if (Object.keys(e).length) {
      setFormErrors(e);
      return;
    }

    setSubmitLoading(true);
    try {
      const payload = {
        plannedLeaveDate: new Date(form.plannedLeaveDate).toISOString(),
        reasonForVacating: form.reasonForVacating,
      };
      const res = await createVacateNotice(payload);
      if (res.statusCode === 1) {
        toast.success(res.message || "Vacate notice submitted successfully.");
        fetchVacateNotice();
      } else {
        toast.error(res.message || "Failed to submit vacate notice.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancelNotice = async () => {
    setCancelLoading(true);
    try {
      const res = await cancelVacateNotice(vacateData.id);
      if (res.statusCode === 1) {
        toast.success(res.message || "Vacate request cancelled.");
        setIsModalOpenCancel(false);
        setVacateData(null);
        setForm({ plannedLeaveDate: "", reasonForVacating: "" });
      } else {
        toast.error(res.message || "Failed to cancel.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setCancelLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">Vacate Notice</div>
            <div className="page-sub">Submit or track your vacate request</div>
          </div>
        </div>
        <div className="content">
          <div className="card" style={{ maxWidth: 600, margin: "0 auto" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                padding: 20,
              }}
            >
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    height: 14,
                    borderRadius: 6,
                    background:
                      "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.4s infinite",
                    width: i === 0 ? "40%" : "70%",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">Vacate Notice</div>
            <div className="page-sub">Submit or track your vacate request</div>
          </div>
        </div>

        <div className="content">
          {/* ── No active notice — show form ── */}
          {!vacateData && (
            <div className="card" style={{ maxWidth: 580, margin: "0 auto" }}>
              <div style={{ textAlign: "center", padding: "20px 0 16px" }}>
                <div style={{ fontSize: 40, marginBottom: 8 }}>🚪</div>
                <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>
                  Send Vacate Notice
                </div>
                <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                  Notify your property owner about your plan to vacate
                </div>
              </div>

              <div className="warn-box mb14" style={{ fontSize: 12 }}>
                ⚠ Minimum notice period is &nbsp;
                <strong>
                  {vacateNoticeSettingData?.minNoticeDays ?? 15} days
                </strong>
                . Earliest leave date: <strong>{minDateFormatted}</strong>
              </div>

              <div className="form-group mb14">
                <label>Planned Leave Date *</label>
                <input
                  type="date"
                  name="plannedLeaveDate"
                  value={form.plannedLeaveDate}
                  min={minDateStr}
                  onChange={handleFormChange}
                  style={{
                    fontSize: 14,
                    padding: "10px 12px",
                    ...(formErrors.plannedLeaveDate
                      ? { borderColor: "#dc2626" }
                      : {}),
                  }}
                />
                {formErrors.plannedLeaveDate && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {formErrors.plannedLeaveDate}
                  </span>
                )}
                <div className="input-hint">
                  Must be at least{" "}
                  {vacateNoticeSettingData?.minNoticeDays ?? 15} days from today
                </div>
              </div>

              <div className="form-group mb16">
                <label>
                  Reason for Vacating{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (optional)
                  </span>
                </label>
                <textarea
                  name="reasonForVacating"
                  value={form.reasonForVacating}
                  onChange={handleFormChange}
                  placeholder="e.g. Job relocation, purchasing own home…"
                  style={{ minHeight: 80 }}
                />
              </div>

              <div
                style={{
                  background: "var(--bg-secondary)",
                  borderRadius: 8,
                  padding: "12px 14px",
                  marginBottom: 16,
                  fontSize: 12,
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    marginBottom: 6,
                    color: "var(--text-secondary)",
                  }}
                >
                  What happens after submission:
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <div>1. 📬 Property owner receives your notice</div>
                  <div>2. ✅ Owner approves or rejects</div>
                  <div>
                    3. 📅 If approved: your leave date &amp; free hold date are
                    confirmed
                  </div>
                  <div>4. 💰 Rent continues until your leave date</div>
                  <div>5. 🛏 Bed becomes available from free hold date</div>
                </div>
              </div>

              <div style={{ textAlign: "center", paddingBottom: 8 }}>
                <button
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={submitLoading}
                  style={{ fontSize: 14, padding: "10px 28px" }}
                >
                  {submitLoading ? "Submitting..." : "📨 Send Vacate Notice"}
                </button>
              </div>
            </div>
          )}

          {/* ── Active notice — show status card ── */}
          {vacateData &&
            (() => {
              const badge = getStatusBadge(vacateData.status);
              return (
                <div
                  className="card"
                  style={{ maxWidth: 600, margin: "0 auto" }}
                >
                  <div style={{ textAlign: "center", padding: "20px 0 10px" }}>
                    <div style={{ fontSize: 40, marginBottom: 8 }}>📋</div>
                    <div
                      style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}
                    >
                      Vacate Notice Submitted
                    </div>
                    <div
                      style={{ fontSize: 13, color: "var(--text-secondary)" }}
                    >
                      Your notice is under review
                    </div>
                  </div>

                  <div
                    style={{
                      background: "var(--bg-secondary)",
                      borderRadius: 10,
                      padding: 16,
                      margin: "16px 0",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 12,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "var(--text-tertiary)",
                            textTransform: "uppercase",
                          }}
                        >
                          Request Date
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            marginTop: 2,
                          }}
                        >
                          {formatDate(vacateData.requestDate)}
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "var(--text-tertiary)",
                            textTransform: "uppercase",
                          }}
                        >
                          Planned Leave Date
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            marginTop: 2,
                          }}
                        >
                          {formatDate(vacateData.plannedLeaveDate)}
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "var(--text-tertiary)",
                            textTransform: "uppercase",
                          }}
                        >
                          Notice Period
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            marginTop: 2,
                          }}
                        >
                          {vacateData.noticePeriodDays} days
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "var(--text-tertiary)",
                            textTransform: "uppercase",
                          }}
                        >
                          Owner Remark
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            marginTop: 2,
                          }}
                        >
                          {vacateData.ownerRemark || "Pending approval"}
                        </div>
                      </div>
                    </div>

                    {vacateData.reasonForVacating && (
                      <div
                        style={{
                          marginTop: 12,
                          paddingTop: 12,
                          borderTop: "1px solid var(--border-light)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "var(--text-tertiary)",
                            textTransform: "uppercase",
                          }}
                        >
                          Reason
                        </div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>
                          {vacateData.reasonForVacating}
                        </div>
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 10,
                      marginBottom: 16,
                    }}
                  >
                    <span
                      style={{
                        background: badge.bg,
                        color: badge.color,
                        borderRadius: 99,
                        padding: "4px 12px",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {badge.label}
                    </span>
                    {vacateData.status === 1 && (
                      <span style={{ fontSize: 12, color: "var(--amber)" }}>
                        Waiting for owner approval
                      </span>
                    )}
                  </div>

                  {vacateData.status === 1 && (
                    <div style={{ textAlign: "center", paddingBottom: 8 }}>
                      <button
                        className="btn btn-danger"
                        onClick={() => setIsModalOpenCancel(true)}
                        style={{ fontSize: 12 }}
                      >
                        ↩ Cancel Vacate Request
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
        </div>
      </div>

      {/* ── Cancel Confirm Modal ── */}
      {isModalOpenCancel && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 420 }}>
            <div className="modal-header">
              <div className="modal-title">⚠️ Cancel Vacate Request</div>
              <button
                className="modal-close"
                onClick={() => setIsModalOpenCancel(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p
                style={{
                  fontSize: 14,
                  lineHeight: "1.6",
                  color: "var(--text-primary)",
                }}
              >
                Cancel your vacate request? You will continue as an active
                tenant.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={() => setIsModalOpenCancel(false)}
                disabled={cancelLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleCancelNotice}
                disabled={cancelLoading}
              >
                {cancelLoading ? "Cancelling..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
